/**
 * ArmyPreview Component
 * Displays a preview grid of all deployed creatures as thumbnails
 *
 * Shows creature count in label and renders a responsive grid of 40x40 thumbnails.
 * Handles missing sprites with colored placeholder boxes showing creature initials.
 */

import styles from './ArmyPreview.module.css';

export interface ArmyPreviewCreature {
  creature: {
    id: string;
    name: string;
    sprite: string;
  };
}

export interface ArmyPreviewProps {
  /** Array of deployed creatures to display */
  creatures: ArmyPreviewCreature[];
  /** Label for the army (e.g., "Your Army" or "Enemy Army") */
  label: string;
  /** Which side to align the component to */
  side: 'left' | 'right';
}

/**
 * Generate a consistent color based on creature name
 * Uses simple hash to pick from a palette of colors
 */
function getPlaceholderColor(name: string): string {
  const colors = [
    '#8b5cf6', // purple
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

/**
 * ArmyPreview Component
 *
 * Displays a labeled grid of creature thumbnails for the VS screen.
 */
export function ArmyPreview({ creatures, label, side }: ArmyPreviewProps): JSX.Element {
  const containerClasses = [
    styles.container,
    side === 'right' ? styles.alignRight : styles.alignLeft,
  ].join(' ');

  return (
    <div className={containerClasses}>
      <div className={styles.label}>
        {label} ({creatures.length})
      </div>

      <div className={styles.thumbnailGrid} role="list" aria-label={`${label} creatures`}>
        {creatures.map(({ creature }, index) => {
          const initial = creature.name.charAt(0).toUpperCase();
          const placeholderColor = getPlaceholderColor(creature.name);

          return (
            <div
              key={`${creature.id}_${index}`}
              className={styles.thumbnail}
              role="listitem"
              title={creature.name}
              aria-label={creature.name}
            >
              {creature.sprite ? (
                <img
                  src={creature.sprite}
                  alt={creature.name}
                  className={styles.sprite}
                  onError={(e) => {
                    // Hide broken image and show placeholder
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className={styles.placeholder}
                style={{
                  backgroundColor: placeholderColor,
                  display: creature.sprite ? 'none' : 'flex',
                }}
              >
                {initial}
              </div>
            </div>
          );
        })}

        {creatures.length === 0 && (
          <div className={styles.empty}>No creatures deployed</div>
        )}
      </div>
    </div>
  );
}

export default ArmyPreview;
